'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/ui';
import { Eye } from 'lucide-react';
import type { Product } from '@/types';

interface ProductCardProps {
  product: Product;
  showQuickView?: boolean;
}

export function ProductCard({ product, showQuickView = true }: ProductCardProps) {
  const mainImage = product.images[0]?.url || '/placeholder.png';
  const hasSale = product.salePrice !== undefined;
  const [imageLoading, setImageLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);

  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setButtonLoading(true);
    window.location.href = `/product/${product.id}`;
  };

  return (
    <div className="group relative h-full">
      <Link href={`/product/${product.id}`} className="block h-full">
        <Card className="h-full flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer overflow-hidden border-gray-200 bg-white">
          {/* Image Container - Consistent Aspect Ratio */}
          <div className="aspect-square relative overflow-hidden bg-gray-100">
            {imageLoading && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
            <img
              src={mainImage}
              alt={product.title}
              className={`w-full h-full object-cover transition-transform duration-300 ${
                imageLoading ? 'opacity-0' : 'opacity-100 group-hover:scale-105'
              }`}
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
              loading="lazy"
            />
            {/* Add to Cart Button Overlay */}
            {showQuickView && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full shadow-lg pointer-events-auto md:w-auto"
                  onClick={handleQuickView}
                  disabled={buttonLoading}
                >
                  {buttonLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Ачааллаж байна...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Харах
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
          
          {/* Content - Consistent Spacing */}
          <CardContent className="p-4 flex-1 flex flex-col gap-3">
            {/* Title - Unified Typography */}
            <h3 className="font-semibold text-sm leading-tight line-clamp-2 text-gray-900 group-hover:text-blue-600 transition-colors min-h-[2.5rem]">
              {product.title}
            </h3>
            
            {/* Price - Consistent Typography */}
            <div className="flex items-baseline gap-2 flex-wrap mt-auto">
              {hasSale ? (
                <>
                  <span className="text-xl font-bold text-[#10b981]">
                    {formatCurrency(product.salePrice!)}
                  </span>
                  <span className="text-sm text-gray-400 line-through">
                    {formatCurrency(product.price)}
                  </span>
                </>
              ) : (
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(product.price)}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}

